/* global $ addTeamsRefreshPath */

import { Controller } from '@hotwired/stimulus'
import { Modal } from 'bootstrap'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import { trans, SELECT, REMOVE, SELECT_ALL, REMOVE_ALL } from '../../translator'

export default class extends Controller {
  static targets = [
    'modal', 'modalResults',
    'teams', 'selectedTeams', 'selectedTeamRowTemplate', 'noTeamsContainer',
    'searchInput', 'categoryFilter', 'categoryFilterBadgeContainer', 'categoryFilterBadgeValues'
  ]

  selectedTeamIds = new Set()

  connect () {
    this.teamsTarget.querySelectorAll('option[selected]').forEach(option => {
      this.selectedTeamIds.add(option.value)
    })

    this.initializeCategoryFilterDropdown()
    this.syncCategoryFilterBadge()
  }

  initializeCategoryFilterDropdown () {
    if (!this.categoryFilterTarget || typeof $ === 'undefined') {
      return
    }

    $(this.categoryFilterTarget).dropdown()
  }

  clearCategoryFilters () {
    if (!this.categoryFilterTarget) {
      return
    }

    if (typeof $ !== 'undefined') {
      $(this.categoryFilterTarget).dropdown('clear')
    }

    Array.from(this.categoryFilterTarget.options).forEach(option => {
      option.selected = false
    })

    this.syncCategoryFilterBadge()
    this.search()
  }

  handleCategoryFilterChange () {
    this.syncCategoryFilterBadge()
    this.search()
  }

  removeCategoryFilterBadge () {
    this.clearCategoryFilters()
  }

  syncCategoryFilterBadge () {
    if (!this.categoryFilterTarget || !this.categoryFilterBadgeContainerTarget || !this.categoryFilterBadgeValuesTarget) {
      return
    }

    const selectedLabels = Array.from(this.categoryFilterTarget.querySelectorAll('option:checked')).map(option => option.textContent?.trim()).filter(Boolean)

    if (selectedLabels.length === 0) {
      this.categoryFilterBadgeContainerTarget.style.display = 'none'
      this.categoryFilterBadgeValuesTarget.textContent = ''
      this.categoryFilterBadgeValuesTarget.title = ''
      return
    }

    const truncatedLabels = selectedLabels.length > 2 ? selectedLabels.slice(0, 2).concat('...') : selectedLabels

    this.categoryFilterBadgeValuesTarget.textContent = truncatedLabels.join(', ')
    this.categoryFilterBadgeValuesTarget.title = selectedLabels.join(', ')
    this.categoryFilterBadgeContainerTarget.style.display = ''
  }

  openModal () {
    const modal = new Modal(this.modalTarget)
    modal.show()
    this.search()
  }

  suppressSearchSubmit (event) {
    event.preventDefault()

    if (typeof event.stopPropagation === 'function') {
      event.stopPropagation()
    }

    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation()
    }
  }

  async search () {
    const teams = await this.fetchFilteredTeams()
    this.renderResults(teams)
  }

  async selectAllTeams () {
    const teams = await this.fetchFilteredTeams()

    for (const team of teams) {
      if (!this.selectedTeamIds.has(team.id)) {
        this.selectTeam(team.id, team.name, team.categories)
      }
    }
  }

  async removeAllTeams () {
    const teams = await this.fetchFilteredTeams()

    for (const team of teams) {
      if (this.selectedTeamIds.has(team.id)) {
        this.removeTeam(team.id)
      }
    }
  }

  async fetchFilteredTeams () {
    const search = this.searchInputTarget.value
    const categories = this.hasCategoryFilterTarget
      ? Array.from(this.categoryFilterTarget.querySelectorAll('option:checked')).map(option => option.value)
      : []

    const response = await fetchWithHandlingLoginRedirect(addTeamsRefreshPath, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ search, categories })
    })
    const data = await response.json()

    return data.results || []
  }

  renderResults (teams) {
    this.modalResultsTarget.innerHTML = ''
    this.modalResultsTarget.appendChild(this.buildBulkActionsRow(teams.length))

    for (const team of teams) {
      const tr = document.createElement('tr')
      tr.dataset.teamId = team.id
      const isSelected = this.selectedTeamIds.has(team.id)

      const actionTd = document.createElement('td')
      actionTd.className = 'text-left table-no-follow-link'

      const selectBtn = document.createElement('button')
      selectBtn.type = 'button'
      selectBtn.className = 'btn btn-sm btn-raven-light select-team' + (isSelected ? ' d-none' : '')
      selectBtn.textContent = trans(SELECT, {}, 'actions')
      selectBtn.dataset.teamId = team.id
      selectBtn.dataset.teamName = team.name
      selectBtn.dataset.teamCategories = JSON.stringify(team.categories)
      selectBtn.addEventListener('click', (e) => {
        const btn = e.currentTarget
        this.selectTeam(btn.dataset.teamId, btn.dataset.teamName, JSON.parse(btn.dataset.teamCategories))
      })

      const removeBtn = document.createElement('button')
      removeBtn.type = 'button'
      removeBtn.className = 'btn btn-sm btn-raven-light remove-team-modal' + (isSelected ? '' : ' d-none')
      removeBtn.textContent = trans(REMOVE, {}, 'actions')
      removeBtn.dataset.teamId = team.id
      removeBtn.addEventListener('click', (e) => {
        this.removeTeam(e.currentTarget.dataset.teamId)
      })

      actionTd.appendChild(selectBtn)
      actionTd.appendChild(removeBtn)
      tr.appendChild(actionTd)

      const nameTd = document.createElement('td')
      nameTd.className = 'priority-1'
      nameTd.textContent = team.name
      tr.appendChild(nameTd)

      const catTd = document.createElement('td')
      catTd.className = 'priority-2'
      for (const cat of team.categories) {
        const badge = document.createElement('span')
        badge.className = 'badge badge-primary badge-raven-reg mr-1'
        badge.textContent = cat.full_name
        catTd.appendChild(badge)
      }
      tr.appendChild(catTd)

      this.modalResultsTarget.appendChild(tr)
    }
  }

  buildBulkActionsRow (teamCount) {
    const row = document.createElement('tr')
    const cell = document.createElement('td')
    cell.colSpan = 3
    cell.className = 'priority-1 table-no-follow-link'

    const media = document.createElement('div')
    media.className = 'media align-items-center'

    const actions = document.createElement('div')
    actions.className = 'media-bod align-items-center'

    const selectAllButton = document.createElement('button')
    selectAllButton.type = 'button'
    selectAllButton.className = 'import_all_button btn btn-sm btn-raven-dark'
    selectAllButton.textContent = `${trans(SELECT_ALL, {}, 'actions')} (${teamCount})`
    selectAllButton.addEventListener('click', async () => {
      await this.selectAllTeams()
    })

    const removeAllButton = document.createElement('button')
    removeAllButton.type = 'button'
    removeAllButton.className = 'btn btn-sm btn-raven-dark'
    removeAllButton.textContent = trans(REMOVE_ALL, {}, 'actions')
    removeAllButton.addEventListener('click', async () => {
      await this.removeAllTeams()
    })

    actions.appendChild(selectAllButton)
    actions.appendChild(removeAllButton)
    media.appendChild(actions)
    cell.appendChild(media)
    row.appendChild(cell)

    return row
  }

  selectTeam (teamId, teamName, categories) {
    this.teamsTarget.querySelector('option[value="' + teamId + '"]').setAttribute('selected', 'selected')

    const newRow = this.selectedTeamRowTemplateTarget.cloneNode(true)
    newRow.dataset.teamId = teamId
    newRow.querySelector('.team-name-template').textContent = teamName

    const sorted = [...categories].sort((a, b) => a.full_name.localeCompare(b.full_name))
    newRow.querySelector('.team-categories-template').innerHTML = sorted
      .map(cat => `<span class="badge badge-primary badge-raven-reg mr-1">${cat.full_name}</span>`)
      .join('')

    newRow.querySelector('.remove-team-template').dataset.teamId = teamId
    newRow.classList.remove('d-none')
    newRow.classList.add('selected-team-row')

    this.selectedTeamsTarget.appendChild(newRow)
    this.noTeamsContainerTarget.classList.add('d-none')
    this.selectedTeamIds.add(teamId)

    this.toggleSelectRemoveButtons(teamId)
  }

  removeTeamWithCross (event) {
    this.removeTeam(event.currentTarget.dataset.teamId)
  }

  removeTeam (teamId) {
    this.teamsTarget.querySelector('option[value="' + teamId + '"]').removeAttribute('selected')
    this.selectedTeamsTarget.querySelector('.selected-team-row[data-team-id="' + teamId + '"]').remove()
    this.selectedTeamIds.delete(teamId)
    if (this.selectedTeamIds.size === 0) {
      this.noTeamsContainerTarget.classList.remove('d-none')
    }
    this.toggleSelectRemoveButtons(teamId)
  }

  toggleSelectRemoveButtons (teamId) {
    this.modalResultsTarget.querySelector('tr[data-team-id="' + teamId + '"] .select-team')?.classList?.toggle('d-none')
    this.modalResultsTarget.querySelector('tr[data-team-id="' + teamId + '"] .remove-team-modal')?.classList?.toggle('d-none')
  }
}
